import { getStripe, isStripeConfigured } from '../lib/stripe';
import { getRenewalIntervalStripe } from '../utils/paymentUtils';

/**
 * Stripe Payment Service
 * Handles Stripe Checkout session creation and payment processing
 * 
 * Note: Stripe Checkout session creation requires a backend server with the secret key.
 * This service assumes a backend API endpoint exists at /api/stripe/create-checkout-session
 * or similar. The backend endpoint should:
 * 1. Verify the payment request
 * 2. Create the Stripe Checkout session using the secret key
 * 3. Return the session URL or session ID
 */

/**
 * Convert dollar amount to cents (Stripe uses cents)
 * @param {number} amount - Amount in dollars
 * @returns {number} Amount in cents
 */
export function formatStripeAmount(amount) {
  if (typeof amount !== 'number' || isNaN(amount)) {
    throw new Error('Invalid amount: must be a number');
  }
  return Math.round(amount * 100);
}

/**
 * Convert cents to dollars
 * @param {number} cents - Amount in cents
 * @returns {number} Amount in dollars
 */
export function formatDollarAmount(cents) {
  if (typeof cents !== 'number' || isNaN(cents)) {
    throw new Error('Invalid amount: must be a number');
  }
  return cents / 100;
}

/**
 * Create a Stripe Checkout session
 * This function calls a backend API to create the session
 * 
 * NOTE: This requires a backend server with Stripe secret key.
 * The backend endpoint should be at: /api/stripe/create-checkout-session
 * 
 * @param {Object} params - Checkout session parameters
 * @param {string} params.paymentId - Payment record ID from database
 * @param {number} params.amount - Amount in dollars
 * @param {string} params.currency - Currency code (default: 'usd')
 * @param {string} params.description - Payment description
 * @param {string} params.userId - User ID making the payment
 * @param {string} params.successUrl - URL to redirect after successful payment
 * @param {string} params.cancelUrl - URL to redirect after cancelled payment
 * @param {string} params.paymentType - Payment type: 'one-time', 'subscription', or 'installment'
 * @param {string} params.renewalPeriod - Renewal period for subscriptions: 'weekly', 'monthly', '3months', or 'custom'
 * @param {number} params.renewalIntervalMonths - Number of months for custom renewal periods
 * @returns {Promise<Object>} Checkout session object with url or sessionId
 */
export async function createCheckoutSession({
  paymentId,
  amount,
  currency = 'usd',
  description,
  userId,
  successUrl,
  cancelUrl,
  paymentType = 'one-time',
  renewalPeriod = null,
  renewalIntervalMonths = null,
}) {
  if (!isStripeConfigured) {
    throw new Error('Stripe is not configured. Please set VITE_STRIPE_PUBLISHABLE_KEY.');
  }

  if (!paymentId || !amount || !userId) {
    throw new Error('Missing required parameters: paymentId, amount, and userId are required');
  }

  const amountInCents = formatStripeAmount(amount);
  
  // Default URLs if not provided
  const defaultSuccessUrl = successUrl || `${window.location.origin}/account?payment=success&session_id={CHECKOUT_SESSION_ID}`;
  const defaultCancelUrl = cancelUrl || `${window.location.origin}/account?payment=cancelled`;

  // Prepare request body
  const requestBody = {
    paymentId,
    amount: amountInCents,
    currency: currency.toLowerCase(),
    description,
    userId,
    successUrl: defaultSuccessUrl,
    cancelUrl: defaultCancelUrl,
    paymentType,
  };

  // Add subscription billing interval if this is a subscription
  if (paymentType === 'subscription' && renewalPeriod) {
    try {
      const billingInterval = getRenewalIntervalStripe(renewalPeriod, renewalIntervalMonths);
      requestBody.subscription = {
        billing_interval: billingInterval.interval,
        billing_interval_count: billingInterval.interval_count,
      };
    } catch (error) {
      console.error('Error converting renewal period to Stripe format:', error);
      throw new Error(`Invalid subscription renewal period: ${error.message}`);
    }
  }

  // Try to call Supabase Edge Function
  // First try Supabase function, then fall back to local API if configured
  try {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    
    // Use Supabase Edge Function if Supabase is configured
    const apiUrl = supabaseUrl 
      ? `${supabaseUrl}/functions/v1/create-checkout-session`
      : (import.meta.env.VITE_API_URL || '/api/stripe/create-checkout-session');
    
    const headers = {
      'Content-Type': 'application/json',
    };
    
    // Add Supabase auth header if using Supabase function
    if (supabaseUrl && supabaseAnonKey) {
      headers['Authorization'] = `Bearer ${supabaseAnonKey}`;
      headers['apikey'] = supabaseAnonKey;
    }
    
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to create checkout session' }));
      throw new Error(error.message || 'Failed to create checkout session');
    }

    const session = await response.json();
    return session;
  } catch (error) {
    // If backend is not available, provide helpful error message
    const errorMsg = error.message || '';
    if (errorMsg.includes('Failed to fetch') || errorMsg.includes('NetworkError') || error.name === 'TypeError') {
      console.error('⚠️ Backend API not available. Stripe Checkout requires a backend server.');
      console.error('⚠️ Please implement /api/stripe/create-checkout-session endpoint.');
      throw new Error(
        'Backend API endpoint not available. ' +
        'Please implement /api/stripe/create-checkout-session endpoint with Stripe secret key. ' +
        'See Stripe documentation for creating Checkout sessions.'
      );
    }
    console.error('Error creating Stripe Checkout session:', error);
    throw error;
  }
}

/**
 * Redirect to Stripe Checkout
 * @param {string} sessionUrl - Stripe Checkout session URL
 */
export async function redirectToCheckout(sessionUrl) {
  if (!sessionUrl) {
    throw new Error('Session URL is required');
  }

  if (typeof window === 'undefined') {
    throw new Error('Cannot redirect: window object is not available');
  }

  try {
    // Validate URL format
    try {
      new URL(sessionUrl);
    } catch (urlError) {
      throw new Error('Invalid checkout session URL format');
    }

    // Redirect to Stripe Checkout
    window.location.href = sessionUrl;
  } catch (error) {
    console.error('Error redirecting to Stripe Checkout:', error);
    throw error;
  }
}

/**
 * Get Stripe Checkout session details
 * This would typically call a backend API that uses the Stripe secret key
 * 
 * @param {string} sessionId - Stripe Checkout session ID
 * @returns {Promise<Object>} Session details
 */
export async function getCheckoutSession(sessionId) {
  if (!sessionId) {
    throw new Error('Session ID is required');
  }

  // TODO: Replace with actual backend API call
  // Example: const response = await fetch(`/api/stripe/session/${sessionId}`, { ... })
  
  throw new Error(
    'Backend API endpoint required. Please implement /api/stripe/session/:sessionId. ' +
    'This endpoint should retrieve session details using the Stripe secret key.'
  );
  
  // When backend is implemented:
  /*
  try {
    const response = await fetch(`/api/stripe/session/${sessionId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to retrieve session');
    }

    const session = await response.json();
    return session;
  } catch (error) {
    console.error('Error retrieving Stripe Checkout session:', error);
    throw error;
  }
  */
}

/**
 * Check if a payment is pending and ready for Stripe Checkout
 * @param {Object} payment - Payment record from database
 * @returns {boolean} True if payment can be paid via Stripe
 */
export function canPayWithStripe(payment) {
  return (
    payment &&
    payment.payment_provider === 'stripe' &&
    payment.status === 'pending' &&
    payment.amount > 0
  );
}

/**
 * Get checkout URL from payment metadata or create new session
 * This is a helper function that handles the flow of getting or creating a checkout session
 * 
 * @param {Object} payment - Payment record from database
 * @param {Object} options - Additional options for session creation
 * @returns {Promise<string>} Checkout session URL
 */
export async function getOrCreateCheckoutUrl(payment, options = {}) {
  if (!canPayWithStripe(payment)) {
    throw new Error('Payment cannot be paid with Stripe. Payment must be pending and use Stripe provider.');
  }

  // Validate payment amount
  const amount = parseFloat(payment.amount);
  if (isNaN(amount) || amount <= 0) {
    throw new Error('Invalid payment amount. Amount must be greater than 0.');
  }

  // Check if payment metadata has an existing session URL
  const metadata = payment.metadata || {};
  const existingSessionUrl = metadata.stripe_checkout_url;
  const existingSessionId = metadata.stripe_session_id;

  // If we have a valid session URL and it's not expired, use it
  // Note: In production, you'd want to check if the session is still valid
  // For now, we'll always create a new session to ensure it's valid
  if (existingSessionUrl && existingSessionId) {
    // Optionally check if session is expired (24 hours for Stripe Checkout)
    const sessionCreatedAt = metadata.stripe_session_created_at;
    if (sessionCreatedAt) {
      const sessionAge = Date.now() - new Date(sessionCreatedAt).getTime();
      const maxAge = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
      if (sessionAge < maxAge) {
        // Session is still valid, return existing URL
        return existingSessionUrl;
      }
    }
  }

  // Create a new checkout session
  try {
    const session = await createCheckoutSession({
      paymentId: payment.id,
      amount: amount,
      currency: (payment.currency || 'USD').toLowerCase(),
      description: payment.description || 'Payment',
      userId: payment.user_id,
      successUrl: options.successUrl,
      cancelUrl: options.cancelUrl,
      paymentType: payment.payment_type || 'one-time',
      renewalPeriod: payment.renewal_period || null,
      renewalIntervalMonths: payment.renewal_interval_months || null,
    });

    const checkoutUrl = session.url || session.sessionUrl || session.checkoutUrl;
    if (!checkoutUrl) {
      throw new Error('Checkout session created but no URL returned');
    }

    return checkoutUrl;
  } catch (error) {
    // Provide more context in error message
    if (error.message?.includes('Backend API')) {
      throw new Error(
        'Payment processing requires a backend server. ' +
        'Please contact support or implement the Stripe Checkout session API endpoint.'
      );
    }
    throw error;
  }
}
